import Link from "next/link";
import GrowWealthChevron from "@/app/assets/icons/grow-wealth/chevron-right";

export type FairValueStatus = "Undervalued" | "Fair" | "Overvalued";

export type FairValuePreviewProps = {
    symbol?: string;
    tradingAt?: number;
    fairValue?: number;
    changePct?: number;
    status?: FairValueStatus;
    badgeLabel?: string;
    ctaHref?: string;
    ctaLabel?: string;
    /** 0–100, where the pointer sits on the cheap → expensive bar */
    pointerPosition?: number;
    className?: string;
};

const STATUS_STYLES: Record<
    FairValueStatus,
    { badge: string; dot: string }
> = {
    Undervalued: {
        badge: "bg-[#E8F8EF] text-[#0D9F4A]",
        dot: "bg-[#0D9F4A]",
    },
    Fair: {
        badge: "bg-[#FFF4E5] text-[#D97706]",
        dot: "bg-[#D97706]",
    },
    Overvalued: {
        badge: "bg-[#FEF5EE] text-[#F15D2A]",
        dot: "bg-[#F15D2A]",
    },
};

const formatInr = (value: number) =>
    `₹${value.toLocaleString("en-IN")}`;

/**
 * Figma Preview Card — mobile W 361 / desktop W 420, padding 28, H hug.
 */
const FairValuePreview = ({
    symbol = "TCS - NSE",
    tradingAt = 3890,
    fairValue = 3240,
    changePct = -16.7,
    status = "Overvalued",
    badgeLabel = "30 seconds",
    ctaHref = "/calculators",
    ctaLabel = "Launch Calculator",
    pointerPosition = 82,
    className = "",
}: FairValuePreviewProps) => {
    const statusStyle = STATUS_STYLES[status];
    const isNegative = changePct < 0;
    const clampedPointer = Math.min(100, Math.max(0, pointerPosition));
    const badgeNumber = badgeLabel.replace(/\s*seconds$/i, "").trim();

    return (
        <article
            className={`relative mx-auto flex w-full max-w-[361px] flex-col rounded-2xl border border-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)] bg-white p-7 shadow-[0_12px_40px_rgba(15,23,42,0.08)] md:mx-0 md:max-w-[420px] md:w-[420px] ${className}`.trim()}
        >
            <span className="absolute -right-2 -top-3 z-10 inline-flex -rotate-6 flex-col items-start gap-0.5 rounded-[14px] bg-[#283980] px-4 py-3 text-white shadow-[0_10px_22px_0_rgba(0,0,0,0.25)]">
                <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em]">
                    Takes
                </span>
                <span className="leading-none">
                    <span className="text-base font-bold tracking-tight">
                        {badgeNumber}
                    </span>{" "}
                    <span className="text-[10px] font-semibold tracking-[0.02em]">
                        seconds
                    </span>
                </span>
            </span>

            <div className="flex flex-col gap-1">
                <p className="text-xs font-medium uppercase leading-4 tracking-[0.04em] text-[var(--colors-text-body-text-1,#767676)]">
                    {symbol}
                </p>
                <p className="text-xs font-medium uppercase leading-4 tracking-[0.04em] text-[var(--colors-text-body-text-1,#767676)]">
                    Trading at
                </p>
                <p className="text-[32px] font-bold leading-9 text-[var(--colors-text-header,#1A1A1A)]">
                    {formatInr(tradingAt)}
                </p>
            </div>

            <div
                className="my-5 h-px w-full bg-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)]"
                aria-hidden
            />

            <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-medium uppercase leading-4 tracking-[0.04em] text-[var(--colors-text-body-text-1,#767676)]">
                        Fair value
                    </p>
                    <span
                        className={`inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold leading-4 ${statusStyle.badge}`}
                    >
                        <span
                            className={`size-1.5 rounded-full ${statusStyle.dot}`}
                            aria-hidden
                        />
                        {status}
                    </span>
                </div>
                <div className="flex flex-wrap items-baseline gap-2">
                    <p className="text-[32px] font-bold leading-9 text-[#283980]">
                        {formatInr(fairValue)}
                    </p>
                    <span
                        className={`text-sm font-semibold leading-5 ${
                            isNegative ? "text-[#E11D1D]" : "text-[#0D9F4A]"
                        }`}
                    >
                        {isNegative ? "" : "+"}
                        {changePct}%
                    </span>
                </div>
            </div>

            <div className="mt-5 flex flex-col gap-2">
                <p className="text-[10px] font-medium uppercase leading-3 tracking-[0.08em] text-[var(--colors-text-body-text-1,#767676)]">
                    Where today&apos;s price sits
                </p>
                <div className="relative pt-3">
                    <div
                        className="h-2 w-full rounded-full"
                        style={{
                            background:
                                "linear-gradient(90deg, #0DCC54 0%, #F5A623 50%, #E11D1D 100%)",
                        }}
                        aria-hidden
                    />
                    <span
                        className="absolute top-0 h-4 w-0 -translate-x-1/2 border-l border-dashed border-[#1A1A1A]"
                        style={{ left: `${clampedPointer}%` }}
                        aria-hidden
                    />
                </div>
                <div className="flex justify-between text-[10px] font-semibold uppercase leading-3 tracking-[0.06em] text-[var(--colors-text-body-text-1,#767676)]">
                    <span>Cheap</span>
                    <span>Fair</span>
                    <span>Expensive</span>
                </div>
            </div>

            <div
                className="my-5 h-px w-full bg-[var(--colors-stroke-colors-card-stroke-color,#E6E6E6)]"
                aria-hidden
            />

            <Link
                href={ctaHref}
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-m)] border border-cta-secondary-stroke bg-cta-secondary px-6 py-2.5 text-sm font-medium leading-5 text-cta-secondary-text no-underline transition-colors hover:border-cta-secondary-stroke-hover hover:bg-cta-secondary-hover hover:text-cta-secondary-text-hover md:text-base md:leading-[22px]"
            >
                {ctaLabel}
                <span className="text-cta-secondary-icon">
                    <GrowWealthChevron />
                </span>
            </Link>
        </article>
    );
};

export default FairValuePreview;
